import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';

// Layout Components
import Layout from './components/layout/Layout';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import NotificationToast from './components/NotificationToast';
import OnboardingWizard from './components/OnboardingWizard';
import ConfirmDialogProvider from './components/ConfirmDialogProvider';

// Page Components
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import TaskDetails from './pages/TaskDetails';
import Messages from './pages/Messages';
import Incidents from './pages/Incidents';
import IncidentDetails from './pages/IncidentDetails';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Documents from './pages/Documents';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import AcceptProjectInvite from './pages/AcceptProjectInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';

// Store
import { useAuthStore, useUIStore } from './store';


function App() {
  const { i18n } = useTranslation();
  const { language, theme } = useUIStore();
  const { isAuthenticated, isBootstrapping, bootstrap } = useAuthStore();

  // On first mount, validate any stored token against the API before
  // deciding whether the user is authenticated (survives page refresh).
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Update language when it changes in the store
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Apply theme to document
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <ConfirmDialogProvider>
    <Router>
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <AppRoutes isAuthenticated={isAuthenticated} theme={theme} />
      </div>
    </Router>
    </ConfirmDialogProvider>
  );
}

// Split out from App so useLocation() has a Router ancestor to read from —
// needed for the /accept-invite special case below.
function AppRoutes({ isAuthenticated, theme }: { isAuthenticated: boolean; theme: string }) {
  const location = useLocation();

  // Reachable regardless of auth state: an already-logged-in user might
  // get invited to a SECOND workspace and click the link while signed in
  // elsewhere — without this, the authenticated catch-all route below
  // would bounce them to /dashboard before they ever saw the invite.
  // AcceptInvite.tsx itself branches on auth state (join-in-place vs the
  // classic name/password signup form).
  if (location.pathname === '/accept-invite') {
    return <AcceptInvite />;
  }

  // Same for a project invite: reachable while logged in (join with one click)
  // and while logged out (new-account form / log in first).
  if (location.pathname === '/accept-project-invite') {
    return <AcceptProjectInvite />;
  }

  // After logging in from a page that sent the user to /login?redirect=<path>
  // (e.g. a project invite for an existing account), go back where they came
  // from. Only same-site relative paths are honoured.
  if (isAuthenticated && location.pathname === '/login') {
    const redirect = new URLSearchParams(location.search).get('redirect');
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      return <Navigate to={redirect} replace />;
    }
  }

  if (isAuthenticated) {
    return (
      <>
        <Layout>
          <Sidebar />
          <div className="flex-1 flex flex-col min-w-0">
            <Header />
            <main className={`flex-1 p-3 sm:p-6 overflow-auto ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/tasks/:id" element={<TaskDetails />} />
                <Route path="/incidents" element={<Incidents />} />
                <Route path="/incidents/:id" element={<IncidentDetails />} />
                <Route path="/team" element={<Navigate to="/settings" replace />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Navigate to="/settings" replace />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </main>
          </div>
        </Layout>
        <NotificationToast />
        <OnboardingWizard />
      </>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/accept-project-invite" element={<AcceptProjectInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
